import { FeatureData } from './../model/project';
import { IDKeyTypes } from 'app/model/id-manager';
import { EmptyObject } from './handlers';
import { DashID } from './drawing/dashed-lines';
import { SymbolInfo } from 'app/types/drawing/symbol-set';
import { ObjID } from './../model/id-manager';
import { ComparisonKeys } from './../model/comparisons';
import { LngLatZoom } from './lng-lat-zoom';
export const ReservedAttributeIds = ['pointName', 'segmentName', 'segmentType', 'pointType', 'control-point', 'transition-point'];

export type InputValue = string;
export type CheckboxValue = boolean;
export type AttributeId = ObjID;
export type AttributeOptionId = number | AttributeOptionStringIds;
export type AttributeFeatureType = 'segment' | 'point' | 'suppl';
export type AttributeFeatureName = 'pointName' | 'segmentName';

export type CalcMethod = '' | 'perft' | 'static' | 'material' | 'labor' | 'material-and-labor' | 'qty' | 'qtyperft';
export type SupplCalcMethod = '' | 'pct_cost_subtot' | 'pct_labor_subtot' | 'pct_material_subtot' | 'price_per_pct_ft';
export type AttributeInputType = 'input' | 'checkbox' | 'selector' | 'qty';
export type LinePattern = 'solid' | 'med-dash-space' | 'small' | 'dotted' | 'dash-dot' | 'dash-dot-dot' | 'dash-dot-dot-dot' | 'dash-dash-dot' | 'dash-dash-dot-dot' | 'dash-dash-dot-dot-dot' | 'long-dash' | 'long-dash-dash';
export enum CostKeys { 'materials' = 'materials', 'labor' = 'labor', 'length' = 'length' };
export enum FeatureStringIdEnum { 'pointType' = 1, 'segmentType' = 2, 'pointName' = 3, 'segmentName' = 4 };
export type FeatureStringIds = 'pointType' | 'segmentType';

export enum AttrOptStrIds { 'control-point' = 1, 'transition-point' = 2 }
export type AttributeOptionStringIds = 'control-point' | 'transition-point'
export type AttributeOption = {
  id: ObjID;
  label: string;
  cost?: Costs; // was metrics
  style?: AttributeStyle;
}
export type RGBHexString = string;
export type AttributeStyle = {
  symbol?: SymbolInfo;
  color?: RGBHexString;
  thickness?: any;
  size?: any;
  pattern?: DashID;
}
export type AvailCriteria = {
  id: ObjID;
  type: 'segment' | 'point';
  attribute_id: ObjID; //
  comparison: ComparisonKeys;
  value?: number | string | boolean; //
  option_id?: ObjID;
}
export type AttributeConfigId = number | AttributeFeatureType | AttributeFeatureName;
export type ConfigAttributeDefault = string | boolean | ObjID | SupplDefaultData;
export type ConfigAttribute = {
  id: ObjID;
  label: string;
  input: AttributeInputType;
  calc: CalcMethod | SupplCalcMethod;
  availability?: Array<AvailCriteria>;
  displayAlways: boolean;
  default?: ConfigAttributeDefault;
  options?: Array<AttributeOption>
}
export type PresetType = 'point' | 'segment';
export type PresetID = number;
export type PresetData = FeatureData;

export type PresetSet = {
  point?: Array<Preset>;
  segment?: Array<Preset>;
}

export type Preset = {
  id: PresetID;
  label: string;
  data: PresetData;
}
export type SupplementalAttribute = ConfigAttribute;
export type PricePoint = {
  alias: string,
  format: string | 'money',
  name: string,
  units: 'USD' | 'feet',
  entry: boolean,
  availability: Array<AttributeFeatureType>
}
export type Costs = {
  [key in CostKeys]?: number;
}
export type AttributeInfo = {
  segment: Array<ConfigAttribute>;
  point: Array<ConfigAttribute>;
  suppl: Array<SupplementalAttribute>;
}
export type ProjectConfig = {
  provider: 'alpha solutions - 2022';
  attributes: AttributeInfo; // was fields
  prices: Array<PricePoint>; // was metrics
  presets: PresetSet;
  general: ProjectMetadata;
  //move suppl_fields to attributes
}
export type ProjectMetadata = {
  name?: string;
  id?: number;
  location: LngLatZoom;
}

export type CalcUnits = 'USD' | 'feet' | '';
export type CalcInfo = {
  name: string;
  metric: CostKeys | '';
  alias: string;
  calc: string;
  units: CalcUnits;
  target?: CostKeys;
  distance_based: boolean;
  summary_label_labor?: string;
  summary_label_materials?: string;
  selectable: boolean;
  type: 'attribute';
}
export type SupplDefaultData = {
  [key: string]: number;
}
export type SupplCalcInput = {
  id: string;
  label: string;
}
export type SupplCalcInfo = {
  name: string;
  alias: string;
  calc: string;
  units?: CalcUnits;
  inputs: Array<SupplCalcInput>;
  selectable: boolean;
  type: 'suppl';
}
export type CalcType = 'suppl' | 'attributes';
export class CalcUtil {
  public static GetCalcFromCalcName(target: CalcType, name: string): CalcInfo | SupplCalcInfo | false {
    for (const calc of CalcUtil.GetCalc(target)) {
      if (calc.name === name) return calc;
    }
    return false;
  }
  public static GetCalc(target: CalcType = 'attributes'): Array<CalcInfo> | Array<SupplCalcInfo> {
    switch (target) {
      case 'suppl':
        return CalcUtil.suppl_calcs;
      default:
      case 'attributes':
        return CalcUtil.calcs;
    }
    return CalcUtil.calcs;
  }
  public static GetSupplCalcInputs(calcName: string): Array<SupplCalcInput> {
    const calcs = CalcUtil.GetCalc('suppl') as Array<SupplCalcInfo>;
    for (const calc of calcs) {
      if (calc.name === calcName) {
        return calc.inputs;
      }
    }
    return [];
  }
  public static GetSupplDefault(calcName: string): SupplDefaultData | undefined {
    const inputs = CalcUtil.GetSupplCalcInputs(calcName);
    if (!inputs || inputs.length === 0) return undefined;
    const defaults: SupplDefaultData = {};
    for (const input of inputs) {
      defaults[input.id] = 0;
    }
    return defaults;
  }
  public static MergeSupplDefaults(calcName: string, oldDefaults: any): SupplDefaultData | undefined {
    const supplDefaults = CalcUtil.GetSupplDefault(calcName);
    if (!supplDefaults) return undefined;
    if (typeof oldDefaults !== 'object') {
      return supplDefaults;
    }
    for (let id in supplDefaults) {
      if (!supplDefaults.hasOwnProperty(id)) continue;
      if (!oldDefaults.hasOwnProperty(id)) continue;
      supplDefaults[id] = oldDefaults[id];
    }
    return supplDefaults;
  }
  public static calcs: Array<CalcInfo> = [{
    name: 'qty',
    metric: '',
    alias: 'Quantity',
    calc: 'qty * (labor + materials)',
    units: 'USD',
    distance_based: false,
    selectable: true,
    type: 'attribute'
  }, {
    name: 'qtyperft',
    metric: '',
    alias: 'Quantity Per Foot',
    calc: 'length * (qty * (labor + materials))',
    units: 'USD',
    distance_based: true,
    selectable: true,
    type: 'attribute'
  }, {
    name: 'static',
    metric: '',
    alias: 'Static ',
    calc: 'labor + materials',
    units: 'USD',
    distance_based: false,
    selectable: true,
    type: 'attribute'
  }, {
    name: '_labor_static',
    metric: CostKeys.labor,
    alias: 'Static',
    calc: 'labor',
    units: 'USD',
    distance_based: false,
    selectable: false,
    type: 'attribute'
  }, {
    name: '_materials_static',
    metric: CostKeys.materials,
    alias: 'Static',
    calc: 'materials',
    units: 'USD',
    distance_based: false,
    selectable: false,
    type: 'attribute'

  }, {
    name: 'perft',
    metric: '',
    alias: 'Per Ft',
    calc: 'length * (labor + materials)',
    units: 'USD',
    distance_based: true,
    summary_label_labor: '{labor} / ft',
    summary_label_materials: '{materials} / ft',
    selectable: true,
    type: 'attribute'
  }, {
    name: '_labor_perft',
    metric: CostKeys.labor,
    alias: 'Per Ft',
    calc: '(length * labor)',
    units: 'USD',
    distance_based: true,
    summary_label_labor: '{labor} / ft',
    summary_label_materials: 'N/A',
    selectable: false,
    type: 'attribute'
  }, {
    name: '_materials_perft',
    metric: CostKeys.materials,
    alias: 'Per Ft',
    calc: '(length * materials)',
    units: 'USD',
    distance_based: true,
    summary_label_labor: 'N/A',
    summary_label_materials: 'materials / ft',
    selectable: false,
    type: 'attribute'
  }, {
    name: 'materials_input',
    metric: CostKeys.materials,
    alias: 'Materials Value',
    calc: 'materials',
    units: '',
    target: CostKeys.materials,
    distance_based: false,
    selectable: true,
    type: 'attribute'
  }, {
    name: 'labor_input',
    metric: CostKeys.labor,
    alias: 'Labor Value',
    calc: 'labor',
    units: '',
    target: CostKeys.labor,
    distance_based: false,
    selectable: true,
    type: 'attribute'
  }
  ];
  public static suppl_calcs: Array<SupplCalcInfo> = [{
    name: 'pct_cost_subtot',
    alias: '% Cost Subtotal',
    calc: '{cost * (x / 100.0)',
    inputs: [{
      id: 'x',
      label: "Price"
    }],
    units: 'USD',
    selectable: true,
    type: 'suppl'
  }, {
    name: 'pct_material_subtot',
    alias: '% Material Subtotal',
    calc: 'materials * (x / 100.0 )',
    inputs: [{
      id: 'x',
      label: "Price"
    }],
    units: 'USD',
    selectable: true,
    type: 'suppl'
  }, {
    name: 'pct_labor_subtot',
    alias: '% Labor Subtotal',
    calc: 'labor * (x / 100.0)',
    inputs: [{
      id: 'x',
      label: "Price"
    }],
    units: 'USD',
    selectable: true,
    type: 'suppl'
  }, {
    name: 'price_per_ft',
    alias: '$USD / Length (ft) Subtotal',
    calc: 'x * length',
    inputs: [{
      id: 'x',
      label: "Price"
    }],
    units: 'USD',
    selectable: true,
    type: 'suppl'
  }, {
    name: 'price_per_pct_ft',
    alias: 'USD / % Length (ft) Subtotal',
    calc: '((y / 100.0) * length) * x',
    inputs: [{
      id: 'x',
      label: 'Price'
    }, {
      id: 'y',
      label: 'Percent Length'
    }],
    selectable: true,
    type: 'suppl',
  }]
}
